import { catchWithLog } from 'helper/zod';
import { z } from 'zod';

import type { MissionV2, VehicleV2 } from 'API';
import { ErrorTypeV2 } from 'API';
import {
    AdsStateFieldV2Schema,
    ConnectivityStatusFieldV2Schema,
    DeviationsFieldV2Schema,
    DriverModeFieldV2Schema,
    DrivingStateFieldV2Schema,
    MissionProgressDetailsFieldV2Schema,
    MissionStateFieldV2Schema,
    MissionV2Schema,
    MrmStateFieldV2Schema,
    PositionFieldV2Schema,
    SpeedMsFieldV2Schema,
    VehicleV2Schema,
} from 'APIzod';

/**
 * Utility type that enforces all keys of T are explicitly provided in a zod object shape,
 * preventing accidental omission of fields when constructing safe schema overrides.
 */
type ExactShape<T> = {
    [K in keyof T]-?: z.ZodType<T[K], any, any>;
};

/**
 * Builds a synthetic ErrorV2 with type PARSING_ERROR, used as a fallback when
 * client-side zod parsing fails on a V2 field value. This allows consumers to
 * distinguish a field that failed to parse from one that simply has no value.
 */
const parsingError = () => ({
    __typename: 'ErrorV2' as const,
    raisedAt: new Date().toISOString(),
    type: ErrorTypeV2.PARSING_ERROR,
});

const fieldParsingError = () => ({ __typename: undefined, value: null, error: parsingError(), ttl: null });

/**
 * Wraps a V2 field schema so that when the value fails to parse, the field is
 * preserved with value: null and a synthetic PARSING_ERROR injected into error.
 * If the entire field object is structurally broken, also returns a field with
 * value: null and PARSING_ERROR rather than null.
 */
const makeSafeFieldSchema = <
    T extends {
        __typename: z.ZodType<any>;
        error: z.ZodType<any>;
        ttl: z.ZodType<any>;
        value: z.ZodType<any>;
    },
>(
    shape: T,
    label: string
) => {
    const CATCH_SENTINEL = Symbol('catch');
    return z
        .object({
            __typename: shape.__typename,
            error: shape.error,
            ttl: shape.ttl,
            value: catchWithLog(shape.value, CATCH_SENTINEL as any, label),
        })
        .transform(field => {
            const wasCaught = field.value === CATCH_SENTINEL;
            return {
                ...field,
                value: wasCaught ? null : field.value,
                error: field.error ?? (wasCaught ? parsingError() : null),
            };
        });
};

// Safe top-level schemas — optional fields degrade gracefully on parse failure:
// the field is preserved with value: null and error.type = PARSING_ERROR injected,
// so consumers can distinguish a parse failure from a field with no value.

/**
 * Safe version of VehicleV2Schema.
 * Each optional field uses makeSafeFieldSchema so that a parse failure degrades
 * the value to null and injects a PARSING_ERROR, without affecting other fields
 * or other vehicles in the list.
 */
export const SafeVehicleV2Schema = (): z.ZodType<VehicleV2> => {
    const shape = VehicleV2Schema().shape;
    const safeShape: ExactShape<VehicleV2> = {
        __typename: shape.__typename,
        vin: shape.vin,
        fleetId: shape.fleetId,
        licensePlate: shape.licensePlate,
        name: shape.name,
        timestamp: shape.timestamp,
        latestVehicleUpdate: shape.latestVehicleUpdate,
        owners: shape.owners,
        activeMissionId: shape.activeMissionId,
        adsState: catchWithLog(
            makeSafeFieldSchema(AdsStateFieldV2Schema().shape, 'VehicleV2.adsState').nullish(),
            fieldParsingError(),
            'VehicleV2.adsState'
        ),
        connectivityStatus: catchWithLog(
            makeSafeFieldSchema(ConnectivityStatusFieldV2Schema().shape, 'VehicleV2.connectivityStatus').nullish(),
            fieldParsingError(),
            'VehicleV2.connectivityStatus'
        ),
        deviations: z
            .array(makeSafeFieldSchema(DeviationsFieldV2Schema().shape, 'VehicleV2.deviations[]').nullable())
            .nullish(),
        driverMode: catchWithLog(
            makeSafeFieldSchema(DriverModeFieldV2Schema().shape, 'VehicleV2.driverMode').nullish(),
            fieldParsingError(),
            'VehicleV2.driverMode'
        ),
        drivingState: catchWithLog(
            makeSafeFieldSchema(DrivingStateFieldV2Schema().shape, 'VehicleV2.drivingState').nullish(),
            fieldParsingError(),
            'VehicleV2.drivingState'
        ),
        mrmState: catchWithLog(
            makeSafeFieldSchema(MrmStateFieldV2Schema().shape, 'VehicleV2.mrmState').nullish(),
            fieldParsingError(),
            'VehicleV2.mrmState'
        ),
        position: catchWithLog(
            makeSafeFieldSchema(PositionFieldV2Schema().shape, 'VehicleV2.position').nullish(),
            fieldParsingError(),
            'VehicleV2.position'
        ),
        speedMs: catchWithLog(
            makeSafeFieldSchema(SpeedMsFieldV2Schema().shape, 'VehicleV2.speedMs').nullish(),
            fieldParsingError(),
            'VehicleV2.speedMs'
        ),
    };
    return z.object(safeShape) as unknown as z.ZodType<VehicleV2>;
};

/**
 * Safe version of MissionV2Schema.
 * Each optional field uses makeSafeFieldSchema so that a parse failure degrades
 * the value to null and injects a PARSING_ERROR, without affecting other fields.
 */
export const SafeMissionV2Schema = (): z.ZodType<MissionV2> => {
    const shape = MissionV2Schema().shape;
    const safeShape: ExactShape<MissionV2> = {
        __typename: shape.__typename,
        id: shape.id,
        vin: shape.vin,
        fleetId: shape.fleetId,
        name: shape.name,
        description: catchWithLog(shape.description, '', 'MissionV2.description'),
        timestamp: shape.timestamp,
        events: shape.events,
        version: shape.version,
        route: catchWithLog(shape.route, null, 'MissionV2.route'),
        actualArrivalTime: shape.actualArrivalTime,
        actualStartTime: shape.actualStartTime,
        plannedArrivalTime: shape.plannedArrivalTime,
        progressDetails: catchWithLog(
            makeSafeFieldSchema(MissionProgressDetailsFieldV2Schema().shape, 'MissionV2.progressDetails').nullish(),
            fieldParsingError(),
            'MissionV2.progressDetails'
        ),
        state: catchWithLog(
            makeSafeFieldSchema(MissionStateFieldV2Schema().shape, 'MissionV2.state').nullish(),
            fieldParsingError(),
            'MissionV2.state'
        ),
    };
    return z.object(safeShape) as unknown as z.ZodType<MissionV2>;
};
